/* eslint-disable import/no-internal-modules */

/* eslint-disable prefer-destructuring */
import { broker } from '#~/agent/broker.js';
import * as Commands from '#~/agent/commands/index.js';
import type { Proxy } from '#~/agent/types.js';
import {
  EVENT_CATEGORIES,
  EVENT_NAMES,
  useAnalytics,
  WORKSPACE_PROPERTIES
} from '#~/analytics/index.js';
import type { SignedUrl } from '#~/api/control/preset/types.js';
import { toHexFromId, toIdFromHex, wrapApiCall } from '#~/api/index.js';
import { useAuth0 } from '#~/auth/index.js';
import { setCurrentUser } from '#~/auth/synchronizer.js';
import { useDepot } from '#~/depot/hooks.js';
import { ElectronAPI } from '#~/electron/index.js';
// ENG-4154: read the IdentityState store, not a per-consumer bridge poll.
import { resolveLinkedAgentId } from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import { nats } from '#~/nats/index.js';
import { post as postNotification } from '#~/notifications/utils.js';
import { Paths } from '#~/routes/index.js';
import type { Api } from '@studio/design-system/dialog';
import { ensureNotNil } from '@studio/js-utils';
import type { Room } from '@studio/models/room';
import type { User } from '@studio/models/user';
import { useLazyRef, χref } from '@studio/react-utils/hooks';
import { UserRegistryUpdateUserRequest } from '@tryiris-ai/common.protojs/api/v1/user_registry';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import { tokenize } from '@tryiris-ai/js-types';
import {
  AgentRegistryDeleteOrganization,
  AgentRegistryDeleteRoom,
  AgentRegistryDestroyMembership,
  AgentRegistryDestroyPhoto,
  AgentRegistryUpdateOrganization,
  AgentRegistryUpdateRoom,
  AgentRegistryUploadPhoto
} from '@tryiris-ai/mailroom-helper/agentRegistry';
import {
  UserRegistryChangePassword,
  UserRegistryDeleteUser,
  UserRegistryDestroyPhoto,
  UserRegistryEnrollMFA,
  UserRegistryUnenrollMFA,
  UserRegistryUpdateUser,
  UserRegistryUploadPhoto
} from '@tryiris-ai/mailroom-helper/userRegistry';
import { toString } from 'lodash-es';
import type { RefObject } from 'react';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { Context } from './context.js';
import type { Wiring } from './types.js';

/** @internal */
export const useWiring = (): Wiring => ensureNotNil(useContext(Context));

/* @internal `cleanupAgents` */
const cleanupAgents = async (token: string, roomId?: Room['id']): Promise<void> => {
  const machineAgentId = isElectronApp() ? await resolveLinkedAgentId() : undefined;

  let currentMachineAgent: T_Maybe<Proxy>;

  try {
    // 1) get all of the agents
    for (const agent of broker) {
      // Defensive check: ensure agent has required properties
      if (!agent || !agent.id || !agent.roomId) {
        console.warn('___cleanupAgents: Skipping invalid agent:', agent);
        continue;
      }

      // If roomId is specified, only process agents from that specific room
      if (roomId && agent.roomId !== roomId) {
        continue;
      }

      if (agent.id === machineAgentId) {
        currentMachineAgent = agent;
        continue;
      }

      // 2) unlink the agents
      try {
        const cmd =
          agent.getStatus() === 'online'
            ? Commands.unlinkDirect(agent.id)
            : Commands.unlinkCloud(agent.id, token);

        await cmd;
      } catch (error) {
        console.error(`___cleanupAgents: Failed to unlink agent ${agent.id}:`, error);
      }
    }

    // 3) unlink the machine agent (only if it belongs to the specified room or no room filter is applied)
    if (currentMachineAgent && (!roomId || currentMachineAgent.roomId === roomId)) {
      const agent = currentMachineAgent;
      try {
        const cmd =
          agent.getStatus() === 'online'
            ? Commands.unlinkDirect(agent.id)
            : Commands.unlinkCloud(agent.id, token);

        await cmd;
      } catch (error) {
        console.error(`___cleanupAgents: Failed to unlink machine agent ${agent.id}:`, error);
      }
    }
  } catch (error) {
    console.error('cleanupAgents: Unexpected error during cleanup:', error);
    throw error;
  }
};

/** @internal */
export const useWorkflow = (apiRef: RefObject<Api | null>): Wiring => {
  const [_hasStudioUser, produce] = useDepot((µ) => [!!µ.user, µ.produce], shallow);

  const { getAccessTokenSilently } = useAuth0();
  const { track } = useAnalytics();

  const commitUserUpdate = async (user: T_Optional<User & { fullName: string }>): Promise<void> => {
    const token = await getAccessTokenSilently();

    const idBytes = toIdFromHex(user.id)!;

    const request = UserRegistryUpdateUserRequest.fromPartial({
      id: idBytes,
      name: user.fullName,
      email: user.email,
      profilePhotoUrl: user.profilePhotoUrl,
      mfaEnabled: user.mfaEnabled,
      token
    });

    try {
      await wrapApiCall(async () =>
        UserRegistryUpdateUser.request(nats.cloudConnection(), request)
      );
    } catch (error) {
      throw new Error(`Failed to update user: ${(<Error>error).message}`);
    }
  };

  const handleUpdateUser = async (user: T_Optional<User & { fullName: string }>): Promise<void> => {
    let id: T_Maybe<User['id']>;

    produce((draft) => {
      if (user.email) {
        draft.user!.email = user.email;
        track(EVENT_NAMES.USER_UPDATE_EMAIL, EVENT_CATEGORIES.ACCOUNT_SETTINGS);
      }

      draft.user!.profilePhotoUrl = user.profilePhotoUrl;

      if (user.fullName) {
        const name = tokenize(toString(user.fullName));
        const [firstName] = name;
        const lastName = name.slice(1).join(' ');

        if (firstName) {
          draft.user!.firstName = firstName;
        }

        draft.user!.lastName = lastName;

        track(EVENT_NAMES.USER_UPDATE_NAME, EVENT_CATEGORIES.ACCOUNT_SETTINGS);
      }

      id = draft.user!.id;

      setCurrentUser({ ...draft.user! });
    });

    await commitUserUpdate({ id, ...user });
  };

  const navigate = useNavigate();

  const handleDeleteUser = async (user: Pick<User, 'id'>): Promise<void> => {
    const token = await getAccessTokenSilently();

    const idBytes = toIdFromHex(user.id)!;

    await wrapApiCall(async () =>
      UserRegistryDeleteUser.request(nats.cloudConnection(), { id: idBytes, token })
    );

    track(EVENT_NAMES.USER_DELETE_ACCOUNT, EVENT_CATEGORIES.ACCOUNT_SETTINGS);

    // Log out ONLY after the delete actually succeeds. This was briefly a
    // `finally` block (PR #357), so navigate ran on every outcome — a failed
    // delete (Stripe rate-limit, partial cascade, any 5xx) still bounced the
    // user to /logout, leaving them believing the account was gone when it
    // wasn't and without the session needed to retry. A plain sequential await
    // short-circuits on rejection before navigate, so failures keep the user on
    // the settings page; the backend M2 idempotent-retry path makes a repeated
    // delete safe. (Devin review, ENG-3781)
    navigate(`/${Paths.logout}`, {
      state: {
        redirect: '/'
      }
    });
  };

  const handleChangePassword = async (user: User): Promise<void> => {
    const token = await getAccessTokenSilently();

    const idBytes = toIdFromHex(user.id)!;

    try {
      const response = await wrapApiCall(async () =>
        UserRegistryChangePassword.request(nats.cloudConnection(), { id: idBytes, token })
      );

      const { url } = response;

      // Track the change password event
      track(EVENT_NAMES.CHANGE_PASSWORD, EVENT_CATEGORIES.SECURITY);

      ElectronAPI.openUrl(url);
    } catch (error) {
      throw new Error(`Failed to change password: ${(<Error>error).message}`);
    }
  };

  const handleEnrollMFA = async (user: User): Promise<void> => {
    const token = await getAccessTokenSilently();

    const idBytes = toIdFromHex(user.id)!;

    try {
      const response = await wrapApiCall(async () =>
        UserRegistryEnrollMFA.request(nats.cloudConnection(), { id: idBytes, token })
      );

      const { url } = response;

      // Track the enable 2FA event
      track(EVENT_NAMES.ENABLE_2FA, EVENT_CATEGORIES.SECURITY);

      ElectronAPI.openUrl(url);
    } catch (error) {
      postNotification({
        actionable: true,
        title: 'Failed to enroll MFA',
        message: 'Failed to enroll MFA',
        severity: 'error'
      });
      throw new Error(`Failed to enroll MFA: ${(<Error>error).message}`);
    }
  };

  const handleUnenrollMFA = async (user: User): Promise<void> => {
    const token = await getAccessTokenSilently();

    const idBytes = toIdFromHex(user.id)!;

    try {
      await wrapApiCall(async () =>
        UserRegistryUnenrollMFA.request(nats.cloudConnection(), { id: idBytes, token })
      );

      produce((draft) => {
        draft.user!.mfaEnabled = false;
      });

      // Track the disable 2FA event
      track(EVENT_NAMES.DISABLE_2FA, EVENT_CATEGORIES.SECURITY);
    } catch (error) {
      throw new Error(`Failed to enroll MFA: ${(<Error>error).message}`);
    }
  };

  const handleUpdateOrganization = async (organization: {
    id: Uint8Array;
    industry: string;
    name: string;
    photoUrl: T_Maybe<string>;
    website: string;
  }): Promise<void> => {
    const token = await getAccessTokenSilently();

    try {
      await wrapApiCall(async () =>
        AgentRegistryUpdateOrganization.request(nats.cloudConnection(), {
          id: organization.id,
          industry: organization.industry,
          name: organization.name,
          profilePhotoUrl: organization.photoUrl,
          website: organization.website,
          token
        })
      );

      // Track organization info update
      track(EVENT_NAMES.UPDATE_ORGANIZATION_INFO, EVENT_CATEGORIES.WORKSPACE, {
        [WORKSPACE_PROPERTIES.ORG_ID]: toHexFromId(organization.id),
        [WORKSPACE_PROPERTIES.ORG_NAME]: organization.name,
        [WORKSPACE_PROPERTIES.WEBSITE]: organization.website,
        [WORKSPACE_PROPERTIES.INDUSTRY]: organization.industry
      });
    } catch (error) {
      throw new Error(`Failed to update organization: ${(<Error>error).message}`);
    }
  };

  const handleUpdateRoom = async (room: Room): Promise<void> => {
    const token = await getAccessTokenSilently();

    const idBytes = toIdFromHex(room.id)!;

    try {
      await wrapApiCall(async () =>
        AgentRegistryUpdateRoom.request(nats.cloudConnection(), { ...room, id: idBytes, token })
      );

      // Track studio name update
      if (room.name) {
        track(EVENT_NAMES.UPDATE_STUDIO_NAME, EVENT_CATEGORIES.WORKSPACE, {
          [WORKSPACE_PROPERTIES.STUDIO_ID]: room.id,
          [WORKSPACE_PROPERTIES.STUDIO_NAME]: room.name
        });
      }
    } catch (error) {
      throw new Error(`Failed to update room: ${(<Error>error).message}`);
    }
  };

  const handleSafeRestart = async (): Promise<void> => {
    if (isElectronApp()) {
      ElectronAPI.restart();
    }

    window.location.href = '/';
  };

  const handleDeleteOrganization = async (organization: { id: Uint8Array }): Promise<void> => {
    const token = await getAccessTokenSilently();

    try {
      await wrapApiCall(async () =>
        AgentRegistryDeleteOrganization.request(nats.cloudConnection(), {
          id: organization.id,
          token
        })
      );

      // Track organization deletion
      track(EVENT_NAMES.DELETE_ORGANIZATION, EVENT_CATEGORIES.WORKSPACE, {
        [WORKSPACE_PROPERTIES.ORG_ID]: toHexFromId(organization.id)
      });

      // Cleanup all agents when deleting entire organization
      await cleanupAgents(token);
    } catch (error) {
      throw new Error(`Failed to delete organization: ${(<Error>error).message}`);
    } finally {
      handleSafeRestart();
    }
  };

  const handleDeleteRoom = async (room: Pick<Room, 'id'>): Promise<void> => {
    const token = await getAccessTokenSilently();

    // ! IMPORTANT: This needs to be done invoked *before* we send a request to delete the room.
    track(EVENT_NAMES.DELETE_STUDIO, EVENT_CATEGORIES.WORKSPACE, {
      [WORKSPACE_PROPERTIES.STUDIO_ID]: room.id
    });

    setTimeout(async () => {
      try {
        await wrapApiCall(async () =>
          AgentRegistryDeleteRoom.request(nats.cloudConnection(), {
            id: toIdFromHex(room.id)!,
            token
          })
        );

        // Only cleanup agents for the specific room being deleted
        await cleanupAgents(token, room.id);
      } catch (error) {
        throw new Error(`Failed to delete room: ${(<Error>error).message}`);
      } finally {
        handleSafeRestart();
      }
    }, 1_000);
  };

  const createSignedOrgPhotoUrl = async (
    organizationId: Uint8Array,
    size: number
  ): Promise<SignedUrl> => {
    const token = await getAccessTokenSilently();

    const result = await wrapApiCall(async () =>
      AgentRegistryUploadPhoto.request(nats.cloudConnection(), {
        organizationId,
        sizeBytes: size,
        token
      })
    );

    return {
      headers: result.signedUploadHeaders,
      publicUrl: result.publicAccessUrl,
      uploadUrl: window.atob(result.signedUploadUrl)
    };
  };

  const createSignedProfilePhotoUrl = async (size: number): Promise<SignedUrl> => {
    const token = await getAccessTokenSilently();

    const result = await wrapApiCall(async () =>
      UserRegistryUploadPhoto.request(nats.cloudConnection(), {
        sizeBytes: size,
        token
      })
    );

    return {
      headers: result.signedUploadHeaders,
      publicUrl: result.publicAccessUrl,
      uploadUrl: window.atob(result.signedUploadUrl)
    };
  };

  const handleUploadOrganizationPhoto = async (organization: {
    id: Uint8Array;
    photo: File;
  }): Promise<string> => {
    const signedUrl = await createSignedOrgPhotoUrl(organization.id, organization.photo.size);

    const response = await fetch(signedUrl.uploadUrl, {
      body: organization.photo,
      headers: signedUrl.headers,
      method: 'PUT'
    });

    if (response.status !== 200) {
      throw new Error(`UT-816/${response.status}`);
    }

    track(EVENT_NAMES.UPLOAD_ORG_PROFILE_PICTURE, EVENT_CATEGORIES.WORKSPACE, {
      [WORKSPACE_PROPERTIES.ORG_ID]: toHexFromId(organization.id),
      [WORKSPACE_PROPERTIES.PHOTO_URL]: signedUrl.publicUrl
    });

    return `${signedUrl.publicUrl}?${Date.now()}`;
  };

  const handleDestoryOrganizationPhoto = async (organization: {
    id: Uint8Array;
  }): Promise<void> => {
    const token = await getAccessTokenSilently();

    try {
      await wrapApiCall(async () =>
        AgentRegistryDestroyPhoto.request(nats.cloudConnection(), {
          organizationId: organization.id,
          token
        })
      );
    } catch (error) {
      throw new Error(`Failed to delete organization photo: ${(<Error>error).message}`);
    }
  };

  const handleUploadProfilePhoto = async (user: { photo: File }): Promise<string> => {
    const signedUrl = await createSignedProfilePhotoUrl(user.photo.size);

    const response = await fetch(signedUrl.uploadUrl, {
      body: user.photo,
      headers: signedUrl.headers,
      method: 'PUT'
    });

    if (response.status !== 200) {
      throw new Error(`UT-816/${response.status}`);
    }

    track(EVENT_NAMES.USER_UPLOAD_PROFILE_PHOTO, EVENT_CATEGORIES.ACCOUNT_SETTINGS);

    return `${signedUrl.publicUrl}?${Date.now()}`;
  };

  const handleDestroyProfilePhoto = async (): Promise<void> => {
    const token = await getAccessTokenSilently();

    try {
      await wrapApiCall(async () =>
        UserRegistryDestroyPhoto.request(nats.cloudConnection(), {
          token
        })
      );
    } catch (error) {
      throw new Error(`Failed to delete organization photo: ${(<Error>error).message}`);
    }
  };

  const [roomRecords] = useDepot((µ) => [µ.roomRecords], shallow);

  const handleLeaveRoom = async (room: Pick<Room, 'id'>): Promise<void> => {
    const token = await getAccessTokenSilently();

    try {
      const roomRecord = roomRecords[room.id];

      await wrapApiCall(async () =>
        AgentRegistryDestroyMembership.request(nats.cloudConnection(), {
          id: roomRecord!.permissions.membershipId!,
          token
        })
      );

      window.location.href = '/';
    } catch (error) {
      throw new Error(`Failed to leave room: ${(<Error>error).message}`);
    }
  };

  return useLazyRef(() => ({
    handleUpdateUser,
    handleLeaveRoom,
    handleDeleteUser,
    handleChangePassword,
    handleEnrollMFA,
    handleUnenrollMFA,
    handleUpdateRoom,
    handleUpdateOrganization,
    handleDeleteRoom,
    handleDeleteOrganization,
    handleUploadOrganizationPhoto,
    handleDestoryOrganizationPhoto,
    handleUploadProfilePhoto,
    handleDestroyProfilePhoto,
    handleClose(): void {
      χref(apiRef)?.close(true);
    }
  })).current;
};
